import { motion } from "framer-motion";
import { Target, Clock, ShieldAlert, Sparkles } from "lucide-react";

export default function Methodology() {
  const steps = [
    {
      icon: <Target className="h-5 w-5 text-[#00E5FF]" />,
      title: "Identify the breakout",
      body: "For every 1-hour candle we check the round numbers ₹100 · 200 · 500 · 1,000 · 5,000 · 10,000. A signal fires when candle low ≤ level AND candle close > level.",
    },
    {
      icon: <ShieldAlert className="h-5 w-5 text-[#FFB020]" />,
      title: "Stop-loss = candle low",
      body: "The low of the signal candle becomes the invalidation. If any subsequent candle prints below it, we exit at the stop-loss.",
    },
    {
      icon: <Clock className="h-5 w-5 text-[#39FF14]" />,
      title: "Hold for 1 week",
      body: "If SL is never hit, we hold for 35 one-hour candles (≈ 5 NSE trading days) and exit at the closing price of the last candle in the window.",
    },
    {
      icon: <Sparkles className="h-5 w-5 text-[#00E5FF]" />,
      title: "Aggregate the edge",
      body: "Win rate, average P&L, best / worst trade, and max drawdown are computed per level and across the whole NSE equity universe (~750 stocks), so you can see which round numbers actually matter.",
    },
  ];

  return (
    <section id="methodology" className="relative py-16 lg:py-24 border-t border-white/8" data-testid="methodology-section">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="max-w-2xl mb-12">
          <div className="text-[10px] font-mono tracking-widest text-white/60 uppercase">04 · Methodology</div>
          <h2 className="mt-2 font-display text-4xl lg:text-5xl font-semibold">
            No black boxes. <span className="text-white/50">Just rules.</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {steps.map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08, duration: 0.4 }}
              className="card-surface rounded-2xl p-6"
              data-testid={`methodology-step-${i}`}
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="h-10 w-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center">
                  {s.icon}
                </div>
                <span className="font-mono text-[10px] tracking-widest text-white/45">STEP {String(i + 1).padStart(2, "0")}</span>
              </div>
              <h3 className="font-display text-xl font-semibold mb-2">{s.title}</h3>
              <p className="text-sm text-white/60 leading-relaxed">{s.body}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-10 card-surface rounded-2xl p-6 flex flex-col md:flex-row md:items-center gap-4 justify-between">
          <div className="text-sm text-white/60">
            <span className="text-white">Disclaimer.</span> Historical backtest results are computed on Yahoo Finance 1H data.
            This is educational research, not investment advice. Slippage, taxes, and lot sizing are not modelled.
          </div>
          <span className="ticker-badge">DATA · YFINANCE</span>
        </div>
      </div>
    </section>
  );
}
